import { useEffect, useState } from 'react';
import type { FigureLayout } from '#shared/core/card-geometry';
import type { PortraitArtRef } from '#shared/schemas/media';
import { buildSticker, measureSticker } from './sticker';

/**
 * Which picture goes in the card's figure slot, and how to get it.
 *
 *  - sticker:   a finished sticker PNG with its layout (the normal case)
 *  - bake:      a cutout with no sticker yet (derivatives not uploaded). Baked in memory.
 *  - window:    no cutout at all (the cutout step failed): the art itself, clipped to the window
 *  - mannequin: no portrait: the built-in silhouette
 *  - pending:   a bake or a measure is running; the slot stays empty for a moment
 */
export type FigureArt =
  | { kind: 'sticker'; href: string; layout: FigureLayout }
  | { kind: 'window'; href: string }
  | { kind: 'mannequin' }
  | { kind: 'pending' };

export type CardSize = 'l' | 'm' | 's';

/** Pixel height of the sticker baked for each card size. */
const BAKE_H: Record<CardSize, number> = { l: 900, m: 450, s: 360 };

/** The sticker file for a card size: L wants the full one, M and S the 360 px thumb. */
export function pickStickerUrl(p: PortraitArtRef, size: CardSize): string | null {
  return size === 'l' ? (p.stickerUrl ?? p.thumbUrl) : (p.thumbUrl ?? p.stickerUrl);
}

export type ArtPlan =
  | { kind: 'sticker'; href: string; layout: FigureLayout }
  | { kind: 'measure'; href: string }
  | { kind: 'bake'; cutoutUrl: string }
  | { kind: 'window'; href: string }
  | { kind: 'mannequin' };

/** The decision part of the resolution: pure, no I/O. */
export function planArt(p: PortraitArtRef | null | undefined, size: CardSize): ArtPlan {
  if (!p) return { kind: 'mannequin' };
  const sticker = pickStickerUrl(p, size);
  if (sticker) {
    return p.layout ? { kind: 'sticker', href: sticker, layout: p.layout } : { kind: 'measure', href: sticker };
  }
  if (p.cutoutUrl) return { kind: 'bake', cutoutUrl: p.cutoutUrl };
  if (p.artUrl) return { kind: 'window', href: p.artUrl };
  return { kind: 'mannequin' };
}

interface Baked {
  href: string;
  layout: FigureLayout;
}

const CACHE_MAX = 24;
const bakes = new Map<string, Promise<Baked>>();
const measures = new Map<string, Promise<FigureLayout>>();

function remember<T>(map: Map<string, Promise<T>>, key: string, make: () => Promise<T>): Promise<T> {
  const hit = map.get(key);
  if (hit) return hit;
  const p = make();
  map.set(key, p);
  // A failure is not cached, so the next render can try again.
  p.catch(() => map.delete(key));
  if (map.size > CACHE_MAX) {
    const oldest = map.keys().next().value;
    if (oldest !== undefined) map.delete(oldest);
  }
  return p;
}

/** Bake (or reuse) an in-memory sticker for a cutout. */
export function bakeFor(cutoutUrl: string, size: CardSize): Promise<Baked> {
  const h = BAKE_H[size];
  return remember(bakes, `${cutoutUrl}@${h}`, async () => {
    const { blob, layout } = await buildSticker(cutoutUrl, h);
    return { href: URL.createObjectURL(blob), layout };
  });
}

function measureFor(href: string): Promise<FigureLayout> {
  return remember(measures, href, () => measureSticker(href));
}

/** Test hook: forget every baked sticker. */
export function resetArtCache(): void {
  bakes.clear();
  measures.clear();
}

/** Resolve the figure art of a card. Re-renders once when a bake or a measure finishes. */
export function useFigureArt(portrait: PortraitArtRef | null | undefined, size: CardSize): FigureArt {
  const plan = planArt(portrait, size);
  const key = plan.kind === 'bake' ? `bake:${plan.cutoutUrl}` : plan.kind === 'measure' ? `measure:${plan.href}` : '';
  const [done, setDone] = useState<{ key: string; art: FigureArt } | null>(null);
  // Primitives only: the portrait object changes identity on every query refetch.
  const bakeUrl = plan.kind === 'bake' ? plan.cutoutUrl : null;
  const measureHref = plan.kind === 'measure' ? plan.href : null;
  const artUrl = portrait?.artUrl ?? null;

  useEffect(() => {
    if (bakeUrl === null && measureHref === null) return;
    let live = true;
    const fallback: FigureArt = artUrl ? { kind: 'window', href: artUrl } : { kind: 'mannequin' };
    const run =
      bakeUrl !== null
        ? bakeFor(bakeUrl, size).then((b): FigureArt => ({ kind: 'sticker', href: b.href, layout: b.layout }))
        : measureFor(measureHref ?? '').then(
            (layout): FigureArt => ({ kind: 'sticker', href: measureHref ?? '', layout }),
          );
    run
      .catch((): FigureArt => fallback)
      .then((art) => {
        if (live) setDone({ key, art });
      });
    return () => {
      live = false;
    };
  }, [key, size, bakeUrl, measureHref, artUrl]);

  if (plan.kind === 'sticker' || plan.kind === 'window' || plan.kind === 'mannequin') return plan;
  return done && done.key === key ? done.art : { kind: 'pending' };
}
